import React from "react";
import "@/App.css";
import "@/styles/theme.css";
import { BrowserRouter, Routes, Route, Navigate, useLocation } from "react-router-dom";
import { Toaster } from "@/components/ui/sonner";
import Welcome from "@/pages/Welcome";
import Login from "@/pages/Login";
import AccessInfo from "@/pages/AccessInfo";
import AcceptInvite from "@/pages/AcceptInvite";
import Threats from "@/pages/Threats";
import ThreatMap from "@/pages/ThreatMap";
import Home from "@/pages/Home";
import DarkWeb from "@/pages/DarkWeb";
import Brand from "@/pages/Brand";
import AdminLogin from "@/pages/AdminLogin";
import AdminDashboard from "@/pages/AdminDashboard";
import Settings from "@/pages/Settings";
import ActorsDirectory from "@/pages/actors/ActorsDirectory";
import ActorProfile from "@/pages/actors/ActorProfile";
import AdminActors from "@/pages/admin/actors/AdminActors";
import AdminActorEditor from "@/pages/admin/actors/AdminActorEditor";
import BreachMonitor from "@/pages/BreachMonitor";
import Reports from "@/pages/Reports";
import NotFound from "@/pages/NotFound";
import { AnalyticsPage, AttackPage, HuntingPage } from "@/pages/sections";
import Vulnerabilities from "@/pages/Vulnerabilities";
import AppShell from "@/components/layout/AppShell";
import { AuthProvider, useAuth } from "@/context/AuthContext";
import { ThemeProvider } from "@/context/ThemeContext";

const HOME = "/home";

const ProtectedRoute = ({ children }) => {
  const { user } = useAuth();
  return user ? children : <Navigate to="/login" />;
};

const AdminOnlyRoute = ({ children }) => {
  const adminToken = localStorage.getItem('admin_token');
  return adminToken ? children : <Navigate to="/admin/login" />;
};

const PublicRoute = ({ children }) => {
  const { user } = useAuth();
  return !user ? children : <Navigate to={HOME} />;
};

/** Old URLs keep working: the query string (?id=, filters) is carried over. */
function Moved({ to }) {
  const { search } = useLocation();
  return <Navigate to={`${to}${search}`} replace />;
}

/** The retired Briefing page: each of its tabs is now a page of its own. */
const BRIEFING_TABS = {
  active: "/threats", timeline: "/threats", mitre: "/attack", "threat-hunt": "/hunting",
  cves: "/vulnerabilities", analytics: "/analytics", insights: "/analytics",
};
function BriefingMoved() {
  const params = new URLSearchParams(useLocation().search);
  return <Navigate to={BRIEFING_TABS[params.get("tab")] || HOME} replace />;
}

const page = (element, { flush = false } = {}) => (
  <ProtectedRoute>
    <AppShell flush={flush}>{element}</AppShell>
  </ProtectedRoute>
);

function App() {
  return (
    <AuthProvider>
      <ThemeProvider>
        <div className="App">
          <BrowserRouter>
            <Routes>
              <Route path="/" element={<Welcome />} />
              <Route path="/login" element={<PublicRoute><Login /></PublicRoute>} />
              <Route path="/register" element={<PublicRoute><AccessInfo /></PublicRoute>} />
              <Route path="/invite/:token" element={<AcceptInvite />} />

              <Route path="/home" element={page(<Home />)} />
              <Route path="/threats" element={page(<Threats />, { flush: true })} />
              <Route path="/analytics" element={page(<AnalyticsPage />)} />
              <Route path="/map" element={page(<ThreatMap />)} />
              <Route path="/vulnerabilities" element={page(<Vulnerabilities />, { flush: true })} />
              <Route path="/attack" element={page(<AttackPage />)} />
              <Route path="/hunting" element={page(<HuntingPage />)} />
              <Route path="/actors" element={page(<ActorsDirectory />)} />
              <Route path="/actors/:slug" element={page(<ActorProfile />)} />
              <Route path="/exposure/dark-web" element={page(<DarkWeb />)} />
              <Route path="/exposure/credentials" element={page(<BreachMonitor />)} />
              <Route path="/exposure/brand" element={page(<Brand />)} />
              <Route path="/reports" element={page(<Reports />)} />
              <Route path="/settings" element={page(<Settings />)} />
              <Route path="/settings/:section" element={page(<Settings />)} />

              {/* Moved pages */}
              <Route path="/overview" element={<Moved to={HOME} />} />
              <Route path="/inbox" element={<Moved to="/threats" />} />
              <Route path="/dashboard" element={<BriefingMoved />} />
              <Route path="/exposure" element={<Moved to="/exposure/dark-web" />} />
              <Route path="/breach-monitor" element={<Moved to="/exposure/credentials" />} />

              <Route path="/threat-actors" element={<Moved to="/actors" />} />
              <Route path="/admin/actors" element={
                <AdminOnlyRoute>
                  <AppShell variant="admin">
                    <AdminActors />
                  </AppShell>
                </AdminOnlyRoute>
              } />
              <Route path="/admin/actors/:id" element={
                <AdminOnlyRoute>
                  <AppShell variant="admin">
                    <AdminActorEditor />
                  </AppShell>
                </AdminOnlyRoute>
              } />
              <Route path="/admin/login" element={<AdminLogin />} />
              <Route path="/admin/dashboard" element={
                <AdminOnlyRoute>
                  <AppShell variant="admin" flush>
                    <AdminDashboard />
                  </AppShell>
                </AdminOnlyRoute>
              } />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </BrowserRouter>
          <Toaster />
        </div>
      </ThemeProvider>
    </AuthProvider>
  );
}

export default App;
